import { fetchEventSource } from '@microsoft/fetch-event-source'
import { isUnauthorizedError, normalizeError } from '../utils/errors.js'

// CEP statement stream endpoint - proxied through /api/monitor
const DEFAULT_STREAM_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_CEP_STREAM_URL) || '/api/monitor'
const DEFAULT_RETRY = {
  initialDelayMs: 500,    // Start reconnecting after 500ms
  maxDelayMs: 5_000,      // Cap at 5 seconds instead of 15
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

const buildStreamUrl = ({ streamUrl, instanceName, statementName }) => {
  // API endpoint: GET /api/monitor or GET /api/monitor/{statement}
  // Only uses statement name, not instance
  if (statementName) {
    return `${streamUrl.replace(/\/$/, '')}/${encodeURIComponent(statementName)}`
  }
  return streamUrl
}

const shouldRetry = (error) => {
  if (isUnauthorizedError(error)) {
    return false
  }
  const normalized = normalizeError(error)
  if (normalized.code === 'STREAM_CLOSED') {
    // closed unexpectedly, attempt retry
    return true
  }
  if (normalized.status && normalized.status >= 400 && normalized.status < 500) {
    return false
  }
  return true
}

export class StatementStreamService {
  constructor({
    streamUrl = DEFAULT_STREAM_URL,
    getAuthHeaders = () => ({}),
    retry = {},
  } = {}) {
    this.streamUrl = streamUrl
    this.getAuthHeaders = getAuthHeaders
    this.retryConfig = {
      initialDelayMs: retry.initialDelayMs ?? DEFAULT_RETRY.initialDelayMs,
      maxDelayMs: retry.maxDelayMs ?? DEFAULT_RETRY.maxDelayMs,
    }
    this.abortController = null
    this.lastEventId = null
    this.isActive = false
    this.reconnectAttempt = 0
    this.handlers = {}
    this.filters = {
      instanceName: undefined,
      statementName: undefined,
    }
    this.pendingRun = null
  }

  start(options = {}) {
    this.handlers = {
      onEvent: options.onEvent,
      onError: options.onError,
      onStatusChange: options.onStatusChange,
      onReconnectScheduled: options.onReconnectScheduled,
    }
    this.filters = {
      instanceName: options.instanceName,
      statementName: options.statementName,
    }
    this.isActive = true
    this.reconnectAttempt = 0
    this.pendingRun = this.runLoop()
    return () => this.stop()
  }

  async runLoop() {
    while (this.isActive) {
      try {
        await this.connectOnce()
        if (!this.isActive) {
          break
        }
        // If connectOnce resolves naturally, schedule immediate retry.
        this.reconnectAttempt += 1
        const delay = Math.min(
          this.retryConfig.initialDelayMs * 2 ** Math.max(0, this.reconnectAttempt - 1),
          this.retryConfig.maxDelayMs,
        )
        this.handlers.onStatusChange?.('reconnecting', {
          attempt: this.reconnectAttempt,
          delay,
        })
        this.handlers.onReconnectScheduled?.({
          delay,
          attempt: this.reconnectAttempt,
        })
        await wait(delay)
      } catch (error) {
        if (!this.isActive) {
          break
        }

        if (error?.name === 'AbortError') {
          // Abort triggered by update/stop. Reset attempt counter on update.
          if (this.isActive) {
            this.reconnectAttempt = 0
            continue
          }
          break
        }

        const normalized = normalizeError(error)
        this.handlers.onError?.(normalized)

        if (!shouldRetry(normalized)) {
          this.handlers.onStatusChange?.('error', normalized)
          this.isActive = false
          break
        }

        this.reconnectAttempt += 1
        const delay = Math.min(
          this.retryConfig.initialDelayMs * 2 ** Math.max(0, this.reconnectAttempt - 1),
          this.retryConfig.maxDelayMs,
        )
        this.handlers.onStatusChange?.('reconnecting', {
          attempt: this.reconnectAttempt,
          delay,
        })
        this.handlers.onReconnectScheduled?.({
          delay,
          attempt: this.reconnectAttempt,
        })
        await wait(delay)
      }
    }

    this.handlers.onStatusChange?.('stopped')
  }

  async connectOnce() {
    const url = buildStreamUrl({
      streamUrl: this.streamUrl,
      instanceName: this.filters.instanceName,
      statementName: this.filters.statementName,
    })

    const headers = {
      ...this.getAuthHeaders(),
    }

    if (this.lastEventId) {
      headers['Last-Event-ID'] = this.lastEventId
    }

    // Note: Do NOT set Accept header here - fetchEventSource library sets it automatically
    // Setting it manually causes duplicate "text/event-stream, text/event-stream" which causes 406
    headers['Cache-Control'] = 'no-cache'

    this.abortController = new AbortController()

    this.handlers.onStatusChange?.(
      this.reconnectAttempt > 0 ? 'reconnecting' : 'connecting',
      { attempt: this.reconnectAttempt },
    )

    const self = this

    await fetchEventSource(url, {
      method: 'GET',
      headers,
      signal: this.abortController.signal,
      async onopen(response) {
        // Mark as connected immediately when HTTP connection is established
        // This is called as soon as the response headers are received (request is pending)
        if (response.status === 401 || response.status === 403) {
          const error = new Error('Unauthorized')
          error.status = response.status
          throw error
        }

        if (!response.ok) {
          const error = new Error(`Stream responded with ${response.status}`)
          error.status = response.status
          throw error
        }

        // Connection is established - mark as connected immediately
        self.reconnectAttempt = 0
        self.handlers.onStatusChange?.('connected')
      },
      onmessage: (event) => {
        if (!event.data) {
          return
        }

        this.lastEventId = event.id ?? this.lastEventId

        let parsedData = null

        try {
          parsedData = JSON.parse(event.data)
        } catch (_error) {
          parsedData = event.data
        }

        this.handlers.onEvent?.({
          id: event.id ?? null,
          type: event.event ?? 'message',
          data: parsedData,
          raw: event,
          receivedAt: Date.now(),
        })
      },
      onclose: () => {
        const error = new Error('Stream closed unexpectedly')
        error.code = 'STREAM_CLOSED'
        throw error
      },
      onerror: (error) => {
        throw error ?? new Error('Stream encountered an error')
      },
      openWhenHidden: true,
    })
  }

  updateFilters(nextFilters = {}) {
    this.filters = {
      ...this.filters,
      ...nextFilters,
    }
    this.lastEventId = null
    if (this.isActive) {
      this.restart()
    }
  }

  restart() {
    if (!this.isActive) {
      return
    }
    this.reconnectAttempt = 0
    this.abortController?.abort()
  }

  manualReconnect() {
    if (!this.isActive) {
      return
    }
    this.reconnectAttempt = 0
    this.lastEventId = null
    this.abortController?.abort()
  }

  stop() {
    this.isActive = false
    this.abortController?.abort()
    this.abortController = null
    this.lastEventId = null
  }

  getLastEventId() {
    return this.lastEventId
  }
}

export const createStatementStreamService = (options) =>
  new StatementStreamService(options)
